
import React from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Swal from "sweetalert2";

import logo from "./exellentkidslogo.png";
import api from "../../api/api";

const GradesDistributionPdf = ({
  pdfGradeClass,
  pdfSubject,
  pdfPeriod,
  selectedYear,
  token,
  setAlertMessage,
  setAlertSeverity,
  setSnackbarOpen,

  // PDF loading state supplied by the parent component
  pdfSaving = false,
  setPdfSaving = () => {},
}) => {
  const COLORS = {
    navy: "#062A4B",
    deepBlue: "#063A6B",
    black: "#0A0F12",
    gold: "#F6C944",
    goldenYellow: "#EFA81C",
    orange: "#C07500",
    white: "#FFFFFF",
    background: "#F4F7FB",
    border: "#D7E1EC",
    muted: "#64748B",
    text: "#25364A",
    success: "#16803D",
    danger: "#C62828",
  };

  // ---------------------------------------------------------
  // BRANDED ALERT
  // ---------------------------------------------------------

  const showAlert = async (type, title, message) => {
    const isSuccess = type === "success";
    const isInfo = type === "info";

    if (setAlertMessage) setAlertMessage(message);
    if (setAlertSeverity) setAlertSeverity(type);
    if (setSnackbarOpen) setSnackbarOpen(false);

    return Swal.fire({
      icon: isSuccess ? "success" : isInfo ? "info" : "error",
      title,
      text: message,
      confirmButtonText: isSuccess ? "Excellent" : "Understood",
      buttonsStyling: false,
      customClass: {
        popup: "grades-report-alert",
        title: "grades-report-alert-title",
        htmlContainer: "grades-report-alert-message",
        confirmButton: "grades-report-alert-button",
      },
      showClass: {
        popup: "swal2-show",
      },
      hideClass: {
        popup: "swal2-hide",
      },
      allowOutsideClick: true,
      allowEscapeKey: true,
    });
  };

  const alertStyles = `
    .grades-report-alert {
      width: min(420px, calc(100% - 32px)) !important;
      padding: 30px 28px 25px !important;
      border-radius: 18px !important;
      border: 1px solid #E2E8F0 !important;
      font-family: Inter, "Segoe UI", Arial, sans-serif !important;
      box-shadow: 0 24px 70px rgba(6, 42, 75, 0.22) !important;
    }

    .grades-report-alert .swal2-icon {
      margin: 0 auto 18px !important;
    }

    .grades-report-alert-title {
      color: #062A4B !important;
      font-size: 22px !important;
      font-weight: 800 !important;
      letter-spacing: -0.4px !important;
    }

    .grades-report-alert-message {
      color: #64748B !important;
      font-size: 14px !important;
      line-height: 1.7 !important;
    }

    .grades-report-alert-button {
      min-width: 125px !important;
      padding: 12px 23px !important;
      border: 0 !important;
      border-radius: 9px !important;
      background: #062A4B !important;
      color: #FFFFFF !important;
      font-size: 13px !important;
      font-weight: 700 !important;
      cursor: pointer !important;
      box-shadow: 0 5px 12px rgba(6, 42, 75, 0.16) !important;
      transition: transform .2s ease, background .2s ease !important;
    }

    .grades-report-alert-button:hover {
      background: #063A6B !important;
      transform: translateY(-1px);
    }

    .grades-report-alert .swal2-success-ring {
      border-color: rgba(22, 128, 61, .25) !important;
    }
  `;

  const injectAlertStyles = () => {
    if (document.getElementById("grades-report-alert-styles")) return;

    const style = document.createElement("style");
    style.id = "grades-report-alert-styles";
    style.textContent = alertStyles;
    document.head.appendChild(style);
  };

  // ---------------------------------------------------------
  // SAFE HTML
  // ---------------------------------------------------------

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  // ---------------------------------------------------------
  // FETCH ALL FILTERED GRADE RECORDS
  // ---------------------------------------------------------

  const fetchPdfGrades = async () => {
    if (!pdfGradeClass?.id || !pdfSubject?.id || !pdfPeriod?.id) {
      return [];
    }

    const headers = token
      ? { Authorization: `Bearer ${token}` }
      : undefined;

    const allGrades = [];

    let page = 1;
    let hasMore = true;

    try {
      while (hasMore) {
        const response = await api.get(
          "/api/users/grades-distribution/",
          {
            headers,
            params: {
              academic_year: selectedYear?.id,
              grade_class: pdfGradeClass.id,
              subject: pdfSubject.id,
              period: pdfPeriod.id,
              page,
              page_size: 500,
            },
          }
        );

        const data = response.data;

        // Supports paginated and non-paginated responses.
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : [];

        allGrades.push(...rows);

        if (Array.isArray(data)) {
          hasMore = false;
        } else {
          hasMore = Boolean(data?.next) && rows.length > 0;
          page += 1;
        }
      }

      return allGrades;
    } catch (error) {
      console.error(
        "PDF grade retrieval failed:",
        error?.response?.data || error
      );

      throw error;
    }
  };

  // ---------------------------------------------------------
  // GENERATE PDF
  // ---------------------------------------------------------

  const handleDownloadPdf = async () => {
    // Prevent duplicate PDF generation.
    if (pdfSaving) return;

    // Activate the parent button spinner immediately.
    setPdfSaving(true);

    try {
      injectAlertStyles();

      // Validate selections.
      if (!pdfGradeClass || !pdfPeriod || !pdfSubject) {
        await showAlert(
          "error",
          "Incomplete Selection",
          "Please select a Grade, Course, and Period before generating the report."
        );

        return;
      }

      // Fetch every matching record.
      let filteredRows;

      try {
        filteredRows = await fetchPdfGrades();
      } catch (error) {
        console.error("Unable to retrieve PDF grades:", error);

        await showAlert(
          "error",
          "Unable to Load Grades",
          "The grade records could not be retrieved. Please check your connection and try again."
        );

        return;
      }

      if (!filteredRows.length) {
        await showAlert(
          "error",
          "No Grade Records Found",
          "There are no grades available for the selected Grade, Course, and Period."
        );

        return;
      }

      const gradeClassName = pdfGradeClass.name;
      const subjectName = pdfSubject.name;
      const periodName = pdfPeriod.name;

      // -----------------------------------------------------
      // GROUP GRADES BY STUDENT
      // -----------------------------------------------------

      const groupedByStudent = filteredRows.reduce((acc, row) => {
        if (!acc[row.student_name]) {
          acc[row.student_name] = {
            student_name: row.student_name,
            subject: row,
          };
        }

        return acc;
      }, {});

      const studentsArray = Object.values(groupedByStudent);

      const rowsPerPagePdf = 25;
      const totalPages = Math.ceil(
        studentsArray.length / rowsPerPagePdf
      );

      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // -----------------------------------------------------
      // LOAD SCHOOL LOGO
      // -----------------------------------------------------

      const logoImg = new Image();

      logoImg.src = logo;
      logoImg.crossOrigin = "anonymous";

      try {
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;

          if (logoImg.complete && logoImg.naturalWidth > 0) {
            resolve();
          }
        });

        // ---------------------------------------------------
        // GENERATE EACH PDF PAGE
        // ---------------------------------------------------

        for (let page = 0; page < totalPages; page++) {
          const pageStudents = studentsArray.slice(
            page * rowsPerPagePdf,
            (page + 1) * rowsPerPagePdf
          );

          const pdfContainer = document.createElement("div");

          Object.assign(pdfContainer.style, {
            position: "fixed",
            left: "-10000px",
            top: "0",
            width: "800px",
            padding: "28px",
            background: COLORS.white,
            color: COLORS.text,
            fontFamily: "Arial, Helvetica, sans-serif",
            boxSizing: "border-box",
          });

          // -------------------------------------------------
          // REPORT HEADER
          // -------------------------------------------------

          const reportHeader = `
            <div style="
              height:7px;
              margin:-28px -28px 20px;
              background:${COLORS.navy};
              border-bottom:3px solid ${COLORS.gold};
            "></div>

            <div style="
              display:flex;
              align-items:center;
              gap:15px;
              padding-bottom:16px;
              border-bottom:2px solid ${COLORS.navy};
            ">
              <div style="
                width:78px;
                height:78px;
                flex-shrink:0;
                display:flex;
                align-items:center;
                justify-content:center;
                padding:5px;
                border:1px solid ${COLORS.border};
                border-radius:10px;
              ">
                <img
                  src="${logoImg.src}"
                  style="width:100%;height:100%;object-fit:contain;"
                />
              </div>

              <div style="flex:1;text-align:left;min-width:0;">
                <div style="
                  font-size:23px;
                  line-height:1.25;
                  font-weight:800;
                  color:${COLORS.navy};
                  margin-bottom:5px;
                ">
                  Excellent Kids Foundation 
                </div>

                <div style="
                  font-size:9px;
                  font-weight:bold;
                  letter-spacing:1.5px;
                  color:${COLORS.orange};
                  margin-bottom:8px;
                ">
                  ACADEMIC PERFORMANCE RECORDS
                </div>

                <div style="
                  font-size:10px;
                  line-height:1.6;
                  color:${COLORS.muted};
                ">
                  Location: GreenLand Commnity, Upper Johnsonville TownShip - Left Bank 2B, Montserrade County, Liberia
                  <br/>
                  Contact: +231-775-912-321 / +231-881-403-326 / +231-777-506-311
                </div>
              </div>

              <div style="
                width:86px;
                flex-shrink:0;
                text-align:center;
                padding:12px 6px;
                background:${COLORS.navy};
                border-radius:8px;
                color:white;
              ">
                <div style="
                  font-size:9px;
                  color:${COLORS.gold};
                  font-weight:bold;
                  letter-spacing:1px;
                  margin-bottom:7px;
                ">OFFICIAL</div>

                <div style="
                  font-size:10px;
                  line-height:1.4;
                  font-weight:bold;
                ">GRADE<br/>REPORT</div>
              </div>
            </div>

            <div style="
              display:flex;
              align-items:center;
              justify-content:space-between;
              margin:20px 0 14px;
            ">
              <div style="text-align:left;">
                <div style="
                  font-size:9px;
                  font-weight:bold;
                  letter-spacing:1.5px;
                  color:${COLORS.orange};
                  margin-bottom:5px;
                ">STUDENT ASSESSMENT</div>

                <div style="
                  font-size:23px;
                  font-weight:800;
                  color:${COLORS.navy};
                ">Grade Distribution</div>

                <div style="
                  font-size:10px;
                  color:${COLORS.muted};
                  margin-top:5px;
                ">Academic scores and performance summary</div>
              </div>

              <div style="
                background:${COLORS.background};
                border:1px solid ${COLORS.border};
                border-radius:8px;
                padding:11px 16px;
                text-align:center;
              ">
                <div style="
                  font-size:8px;
                  font-weight:bold;
                  letter-spacing:1px;
                  color:${COLORS.muted};
                  margin-bottom:5px;
                ">REPORT PAGE</div>

                <div style="
                  font-size:14px;
                  font-weight:bold;
                  color:${COLORS.navy};
                ">${page + 1} / ${totalPages}</div>
              </div>
            </div>

            <div style="
              display:flex;
              width:100%;
              margin-bottom:14px;
              border:1px solid ${COLORS.border};
              border-radius:7px;
              overflow:hidden;
              text-align:left;
            ">
              ${[
                ["GRADE / CLASS", gradeClassName],
                ["COURSE", subjectName],
                ["PERIOD", periodName],
              ]
                .map(
                  ([label, value], index) => `
                    <div style="
                      width:33.333%;
                      padding:11px 10px;
                      background:${COLORS.background};
                      ${
                        index < 2
                          ? `border-right:1px solid ${COLORS.border};`
                          : ""
                      }
                    ">
                      <div style="
                        font-size:8px;
                        font-weight:bold;
                        letter-spacing:.5px;
                        color:${COLORS.muted};
                        margin-bottom:6px;
                      ">${escapeHtml(label)}</div>

                      <div style="
                        font-size:11px;
                        font-weight:bold;
                        color:${COLORS.navy};
                        overflow-wrap:anywhere;
                      ">${escapeHtml(value)}</div>
                    </div>
                  `
                )
                .join("")}
            </div>

            <div style="
              display:flex;
              gap:9px;
              margin-bottom:14px;
            ">
              <div style="
                flex:1;
                padding:10px 12px;
                background:#F8FAFD;
                border-left:4px solid ${COLORS.gold};
                text-align:left;
              ">
                <div style="
                  font-size:8px;
                  font-weight:bold;
                  color:${COLORS.muted};
                  margin-bottom:5px;
                ">TOTAL STUDENTS</div>

                <div style="
                  font-size:15px;
                  font-weight:bold;
                  color:${COLORS.navy};
                ">${studentsArray.length}</div>
              </div>

              <div style="
                flex:1;
                padding:10px 12px;
                background:#F8FAFD;
                border-left:4px solid ${COLORS.deepBlue};
                text-align:left;
              ">
                <div style="
                  font-size:8px;
                  font-weight:bold;
                  color:${COLORS.muted};
                  margin-bottom:5px;
                ">STUDENTS ON THIS PAGE</div>

                <div style="
                  font-size:15px;
                  font-weight:bold;
                  color:${COLORS.navy};
                ">${pageStudents.length}</div>
              </div>
            </div>
          `;

          // -------------------------------------------------
          // TABLE HEADER
          // -------------------------------------------------

          let tableHtml = `
            <table style="
              width:100%;
              border-collapse:collapse;
              table-layout:fixed;
              font-size:10px;
            ">
              <colgroup>
                <col style="width:6%">
                <col style="width:34%">
                <col style="width:8%">
                <col style="width:10%">
                <col style="width:10%">
                <col style="width:8%">
                <col style="width:9%">
                <col style="width:15%">
              </colgroup>

              <thead>
                <tr style="
                  background:${COLORS.navy};
                  color:#FFFFFF;
                ">
                  ${[
                    "#",
                    "STUDENT",
                    "QUIZ",
                    "ASSIGN.",
                    "PARTIC.",
                    "TEST",
                    "SCALE",
                    "FINAL",
                  ]
                    .map(
                      (heading, index) => `
                        <th style="
                          border:1px solid ${COLORS.navy};
                          padding:10px 3px;
                          height:36px;
                          font-size:8px;
                          letter-spacing:.2px;
                          text-align:${
                            index === 1 ? "left" : "center"
                          };
                          ${
                            index === 1
                              ? "padding-left:8px;"
                              : ""
                          }
                          white-space:nowrap;
                        ">${heading}</th>
                      `
                    )
                    .join("")}
                </tr>
              </thead>

              <tbody>
          `;

          // -------------------------------------------------
          // STUDENT ROWS
          // -------------------------------------------------

          pageStudents.forEach((student, i) => {
            const r = student.subject;

            const final = Number(
              r.final_score ?? r.test_score ?? 0
            );

            const color =
              final < 70 ? COLORS.danger : COLORS.deepBlue;

            const rowBackground =
              i % 2 === 0 ? "#FFFFFF" : "#F4F7FB";

            tableHtml += `
              <tr style="background:${rowBackground};">
                <td style="
                  border:1px solid ${COLORS.border};
                  padding:7px 3px;
                  text-align:center;
                  color:${COLORS.muted};
                ">${page * rowsPerPagePdf + i + 1}</td>

                <td style="
                  border:1px solid ${COLORS.border};
                  padding:7px 8px;
                  text-align:left;
                  font-weight:bold;
                  color:${COLORS.navy};
                  overflow-wrap:anywhere;
                ">${escapeHtml(student.student_name)}</td>

                <td style="
                  border:1px solid ${COLORS.border};
                  padding:7px 3px;
                  text-align:center;
                ">
                  ${escapeHtml(r.quiz_score ?? "")}
                </td>

                <td style="
                  border:1px solid ${COLORS.border};
                  padding:7px 3px;
                  text-align:center;
                ">
                  ${escapeHtml(r.assignment_score ?? "")}
                </td>

                <td style="
                  border:1px solid ${COLORS.border};
                  padding:7px 3px;
                  text-align:center;
                ">
                  ${escapeHtml(r.participation_score ?? "")}
                </td>

                <td style="
                  border:1px solid ${COLORS.border};
                  padding:7px 3px;
                  text-align:center;
                ">
                  ${escapeHtml(r.test_score ?? "")}
                </td>

                <td style="
                  border:1px solid ${COLORS.border};
                  padding:7px 3px;
                  text-align:center;
                ">
                  ${escapeHtml(r.scale_grade ?? "")}
                </td>

                <td style="
                  border:1px solid ${COLORS.border};
                  padding:7px 3px;
                  text-align:center;
                  font-size:11px;
                  font-weight:800;
                  color:${color};
                ">${escapeHtml(final)}</td>
              </tr>
            `;
          });

          // -------------------------------------------------
          // TABLE FOOTER
          // -------------------------------------------------

          tableHtml += `
              </tbody>
            </table>

            <div style="
              margin-top:14px;
              padding:10px 12px;
              background:#FFF9E8;
              border-left:3px solid ${COLORS.gold};
              font-size:9px;
              line-height:1.6;
              color:${COLORS.text};
              text-align:left;
            ">
              <strong style="color:${COLORS.navy};">Important:</strong>
              This report reflects the available grade records for the
              selected class, course, and period. Please contact the school
              administration if clarification is required.
            </div>

            <div style="
              display:flex;
              align-items:center;
              justify-content:space-between;
              gap:10px;
              margin-top:20px;
              padding-top:11px;
              border-top:1px solid ${COLORS.border};
              color:${COLORS.muted};
              font-size:8px;
            ">
              <div style="text-align:left;">
                <strong style="color:${COLORS.navy};">
                  Jana L. Williams Educational Foundation
                </strong>

                <div style="margin-top:4px;">
                  Official Academic Records
                </div>
              </div>

              <div style="text-align:center;">
                Handle student records with care.
              </div>

              <div style="
                text-align:right;
                font-weight:bold;
                color:${COLORS.navy};
              ">Page ${page + 1} of ${totalPages}</div>
            </div>

            <div style="
              height:5px;
              margin:16px -28px -28px;
              background:${COLORS.navy};
              border-top:2px solid ${COLORS.gold};
            "></div>
          `;

          pdfContainer.innerHTML = reportHeader + tableHtml;

          document.body.appendChild(pdfContainer);

          // -------------------------------------------------
          // CONVERT PAGE TO PDF IMAGE
          // -------------------------------------------------

          try {
            await new Promise((resolve) =>
              setTimeout(resolve, 100)
            );

            const canvas = await html2canvas(pdfContainer, {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              backgroundColor: "#FFFFFF",
            });

            const imgData = canvas.toDataURL("image/png");

            const margin = 5;

            const availableWidth = pdfWidth - margin * 2;
            const availableHeight = pdfHeight - margin * 2;

            const imgHeight =
              (canvas.height * availableWidth) / canvas.width;

            const renderHeight = Math.min(
              imgHeight,
              availableHeight
            );

            if (page > 0) pdf.addPage();

            pdf.addImage(
              imgData,
              "PNG",
              margin,
              margin,
              availableWidth,
              renderHeight
            );

            // Release canvas memory.
            canvas.width = 1;
            canvas.height = 1;
          } finally {
            if (document.body.contains(pdfContainer)) {
              document.body.removeChild(pdfContainer);
            }
          }
        }

        // ---------------------------------------------------
        // SAVE PDF
        // ---------------------------------------------------

        const safeFileName = [
          gradeClassName,
          subjectName,
          periodName,
        ]
          .map((value) =>
            String(value ?? "")
              .replace(/[<>:"/\\|?*\x00-\x1F]/g, "_")
              .trim()
          )
          .filter(Boolean)
          .join("_");

        pdf.save(`${safeFileName || "Grade_Distribution"}.pdf`);

        await showAlert(
          "success",
          "Report Generated Successfully",
          `Your ${gradeClassName} ${subjectName} grade report is ready. The PDF has been downloaded.`
        );
      } catch (err) {
        console.error("PDF GENERATION ERROR:", err);

        await showAlert(
          "error",
          "PDF Generation Failed",
          "The report could not be generated. Please try again."
        );
      }
    } finally {
      // Always stop the parent's spinner, including when
      // validation fails or an error occurs.
      setPdfSaving(false);
    }
  };

  // ---------------------------------------------------------
  // HIDDEN DOWNLOAD TRIGGER
  // ---------------------------------------------------------

  return (
    <button
      id="grades-pdf-download"
      type="button"
      style={{ display: "none" }}
      onClick={handleDownloadPdf}
      disabled={pdfSaving}
      aria-hidden="true"
      tabIndex={-1}
    />
  );
};

export default GradesDistributionPdf;
